<template>
  <div>
    <GenericChart
      :request-status="requestStatus"
      :title="title"
      :items="items"
      :item-id="itemId"
      :has-info="false"
      class="make-a-copy"
      v-bind="$attrs"
      @reload="getData"
    />

    <v-data-table
      v-if="hasItems"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      class="table-truncate"
    >
      <template v-slot:[`item.id`]="{ item }">
        <span>{{ item.botId }}</span>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <span>{{ item.name }}</span>
      </template>
      <template v-slot:[`item.total`]="{ item }">
        <span>{{ item.total }}</span>
      </template>
      <template v-slot:[`item.failed`]="{ item }">
        <span>{{ item.failed }}</span>
      </template>
      <template v-slot:[`item.received`]="{ item }">
        <span>{{ item.received }}</span>
      </template>
      <template v-slot:[`item.read`]="{ item }">
        <span>{{ item.read }}</span>
      </template>
      <template v-slot:[`item.campaignStatus`]="{ item }">
        <span>{{ item.campaignStatus }}</span>
      </template>
      <template v-slot:[`item.created`]="{ item }">
        <span>{{ item.created }}</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import GenericChart from '@/components/cards/models/GenericChart';
import blipModel from '@/mixins/blip-model';
import { convertTimeToBrazilianFormatDate } from '@/utils/date';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'BlipCampaignsTable',
  components: {
    GenericChart,
  },
  mixins: [
    blipModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    servicePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: this.$t('components.BlipActiveCampaignComponents.bot'), value: 'botId' },
        { text: this.$t('components.BlipActiveCampaignComponents.campaign'), value: 'name' },
        { text: this.$t('components.BlipActiveCampaignComponents.audit'), value: 'total' },
        { text: this.$t('components.BlipActiveCampaignComponents.failed'), value: 'failed' },
        { text: this.$t('components.BlipActiveCampaignComponents.received'), value: 'received' },
        { text: this.$t('components.BlipActiveCampaignComponents.read'), value: 'read' },
        { text: this.$t('components.BlipActiveCampaignComponents.scheduled'), value: 'campaignStatus' },
        { text: this.$t('components.BlipActiveCampaignComponents.send_date'), value: 'created' },
      ],
      items: [],
      rawSeries: [],
      dialog: false,
      itemsPerPage: 5,
      requestStatus: 'loading',
    };
  },
  computed: {
    ...mapGetters('filter', [
      'getStartDateInMilliseconds',
      'getEndDateInMilliseconds',
    ]),
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    hasItems() {
      return Boolean(this.items.length);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'hasNoBlipBotAndContractFiltered',
      'getAppliedCardFilter',
    ]),
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        this.items = [];
        return;
      }

      const cardFilters = await this.getAppliedCardFilter(this.itemId);

      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
        scheduled: cardFilters?.scheduled?.value === undefined ? false : cardFilters?.scheduled?.value,
        campaignName: cardFilters?.campaign_name?.value,
      })
        .then((responses) => {
          if (!options?.silentRefresh) {
            this.requestStatus = 'loading';
          }

          this.rawSeries = responses;

          if (this.rawSeries.status === 'failure') {
            this.items = [];
            this.requestStatus = 'error';
            return;
          }

          if (this.verifyItemsNull(this.rawSeries)) {
            this.items = [];
            this.requestStatus = 'emptyData';
            return;
          }

          this.rawSeries = this.filterItemsNull(this.rawSeries);

          const series = this.rawSeries.map((obj) => {
            const bot = this.getBotName(obj.to, cardFilters);
            const resourceItems = obj.resource.items.map((item) => {
              return {
                botId: bot,
                name: item.name,
                created: item.created,
                campaignStatus: item.scheduled ? 'Sim' : 'Não',
                total: item.total,
                processed: item.processed,
                received: item.received,
                read: item.read,
                failed: item.failed,
              };
            });

            return resourceItems;
          }).flat();

          const filteredSeries = series
            .filter((item) => item.created)
            .map((item) => {
              const formattedDate = convertTimeToBrazilianFormatDate(item.created);
              return { ...item, created: formattedDate };
            });

          this.items = filteredSeries;
          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.items = [];
          this.requestStatus = error.message;
        });
    },
  },
};
</script>

<style lang="scss" scoped>

.v-select__selection.v-select__selection--comma {
  line-height: 120%;
}

.internal-style {
  width: 100%;
  display: flex;
}

.buttons {
  display: grid;
}

.clean-sla-buttons {
  box-shadow: none;
  border-radius: unset;
}

.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

::v-deep {
  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }
}
</style>
